import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { appelApi, ErreurApi, EVENEMENT_SESSION_EXPIREE, urlApi } from './api';

/** Journal d'audit (phase 5, TF-438) : qui a fait quoi, quand — direction seule. */

export type FamilleAction =
  | 'CONNEXION'
  | 'CONSULTATION'
  | 'EXPORT'
  | 'SAISIE'
  | 'DOCUMENT'
  | 'DECISION'
  | 'FACTURATION'
  | 'REGLAGE'
  | 'SYSTEME';

export type ChoixPeriodeJournal =
  | 'AUJOURDHUI'
  | 'SEPT_JOURS'
  | 'TRENTE_JOURS'
  | 'MOIS'
  | 'MOIS_PRECEDENT'
  | 'ANNEE'
  | 'LIBRE';

export type FormatExportJournal = 'XLSX' | 'CSV';

/** Compte auteur, ou `AUCUN` : les traces sans compte connecté. */
export const SANS_COMPTE = 'AUCUN';

export interface LigneJournal {
  id: string;
  survenuLe: string;
  action: string;
  actionLibelle: string;
  famille: FamilleAction;
  familleLibelle: string;
  auteur: { id: string; nom: string; email: string; profil: string; actif: boolean } | null;
  objet: { type: string | null; libelle: string | null; lien: string | null; identifiant: string | null };
  dossier: { id: string; nom: string; lien: string } | null;
  detail: {
    objet: string | null;
    changements: { champ: string; avant: string; apres: string }[];
    route: string | null;
  };
  adresseIp: string | null;
  appareil: string | null;
  agent: string | null;
}

export interface VueJournal {
  calculeLe: string;
  periode: { choix: ChoixPeriodeJournal; du: string; au: string; libelle: string };
  conservation: {
    ans: number;
    plusAncienne: string | null;
    plusAncienneConserveeJusquau: string | null;
    limite: string;
  };
  compteurs: {
    traces: number;
    auteurs: number;
    sansCompte: number;
    connexionsRefusees: number;
    parFamille: { famille: FamilleAction; libelle: string; traces: number }[];
  };
  lignes: LigneJournal[];
  pagination: { page: number; taille: number; total: number; pages: number };
  options: {
    periodes: { code: ChoixPeriodeJournal; libelle: string }[];
    familles: { code: FamilleAction; libelle: string; traces: number }[];
    actions: { code: string; libelle: string; famille: FamilleAction; traces: number }[];
    utilisateurs: { id: string; nom: string; profil: string; actif: boolean; traces: number }[];
    formats: { code: FormatExportJournal; libelle: string }[];
    exportMax: number;
  };
  dossier: { id: string; nom: string | null } | null;
}

export interface FiltresJournal {
  periode: ChoixPeriodeJournal;
  du: string;
  au: string;
  utilisateur: string;
  famille: string;
  action: string;
  dossier: string;
  recherche: string;
  page: number;
  taille: number;
}

/** Paramètres d'adresse, les mêmes pour la liste et pour l'export. Une valeur vide n'est pas envoyée. */
export function parametresJournal(filtres: Partial<FiltresJournal>): string {
  const parametres = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(filtres)) {
    if (valeur === undefined || valeur === '') continue;
    if ((cle === 'du' || cle === 'au') && filtres.periode !== 'LIBRE') continue;
    parametres.set(cle, String(valeur));
  }
  return parametres.toString();
}

/** Une période libre incomplète ou à l'envers n'est pas demandée. */
export function periodeIncomplete(filtres: Pick<FiltresJournal, 'periode' | 'du' | 'au'>): boolean {
  return filtres.periode === 'LIBRE' && (!filtres.du || !filtres.au || filtres.du > filtres.au);
}

export function useJournalAudit(filtres: FiltresJournal) {
  const chaine = parametresJournal(filtres);
  return useQuery({
    queryKey: ['journal-audit', chaine],
    queryFn: () => appelApi<VueJournal>(`/journal-audit?${chaine}`),
    enabled: !periodeIncomplete(filtres),
    placeholderData: keepPreviousData,
    // Le journal s'écrit en continu : relu chaque minute et au retour sur l'onglet.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Télécharge l'export de la liste filtrée, toutes pages comprises. Il passe par
 * `fetch` plutôt que par un lien : un refus de l'API (rien à exporter, liste
 * trop longue) doit s'afficher à l'écran, pas s'enregistrer comme un faux
 * fichier. Rend le nombre de lignes du fichier.
 */
export async function exporterJournal(
  filtres: FiltresJournal,
  format: FormatExportJournal,
): Promise<{ nomFichier: string; lignes: number }> {
  const sansPagination: Partial<FiltresJournal> = { ...filtres, page: undefined, taille: undefined };
  const reponse = await fetch(urlApi(`/journal-audit/export?${parametresJournal(sansPagination)}`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format }),
  });
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as
      | { erreur?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (reponse.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    throw new ErreurApi(
      reponse.status,
      erreur?.erreur?.code ?? 'ERREUR',
      erreur?.erreur?.message ?? 'L’export n’a pas pu être produit.',
      erreur?.erreur?.details,
    );
  }
  const nomFichier =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ??
    `journal-audit.${format.toLowerCase()}`;
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
  return { nomFichier, lignes: Number(reponse.headers.get('x-lignes') ?? 0) };
}
